import { useMemo, useState } from 'react';
import { useAuthStore } from '@/state/store/auth';
import { useApiKeys } from './use-api-keys';
import { createApiKey, revokeApiKey } from './api-keys.service';
import {
  ArrowsClockwiseIcon,
  KeyIcon,
  MagnifyingGlassIcon,
  SlidersHorizontalIcon,
  PlusIcon,
  ProhibitIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type { ColumnDef } from '@tanstack/react-table';
import { AdvanceDataTable } from '@/components/core/advance-data-table/advance-data-table';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { ApiKeyActionDialog, type KeyAction } from './api-key-dialogs';
import type { ApiKeyScopeGrant } from './api-key-scope-resolver';
import type { ApiKey } from './api-keys.types';
import { ApiKeyScopeRulesDialog } from './api-key-scope-rules-dialog';

const EMPTY_KEYS: ApiKey[] = [];

const PINNING = { left: ['name'], right: [] };
const formatDate = (value: string | null, fallback: string) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Intl.DateTimeFormat('en', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(value))
    : fallback;
const selectClass =
  'h-11 rounded-md border border-input bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function ScopeSummary({ scopes, roles }: { scopes: string[]; roles: string[] }) {
  const resolved = [...new Set(scopes)];
  return (
    <div className="min-w-[220px] max-w-[280px] space-y-1 py-2">
      <details>
        <summary className="cursor-pointer text-sm font-medium text-primary">
          {resolved.length} explicit permissions
        </summary>
        <ul
          className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs text-muted-foreground"
          aria-label="Granted scopes"
        >
          {resolved.map((scope) => (
            <li key={scope}>
              <code>{scope}</code>
            </li>
          ))}
        </ul>
      </details>
      {roles.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">{roles.length} assigned roles</summary>
          <ul className="mt-1 space-y-1">
            {roles.map((role) => (
              <li className="break-all" key={role}>
                {role}
              </li>
            ))}
          </ul>
          <p className="mt-1">Permissions inherited from roles are not included above.</p>
        </details>
      )}
    </div>
  );
}

export function ApiKeysPage() {
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  const userId = useAuthStore((state) => state.user?.itemId ?? null);
  return (
    <ApiKeysList
      key={`${organizationId}:${userId}`}
      organizationId={organizationId}
      userId={userId}
    />
  );
}

function ApiKeysList({
  organizationId,
  userId,
}: {
  organizationId: string | null;
  userId: string | null;
}) {
  const { data, isPending, isError, isFetching, refetch } = useApiKeys(organizationId, userId);
  const keys = data ?? EMPTY_KEYS;
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [action, setAction] = useState<KeyAction | null>(null);
  const [notice, setNotice] = useState('');
  const [manageScopes, setManageScopes] = useState(false);
  const rows = useMemo(
    () =>
      keys.filter(
        (key) =>
          (status === 'all' || key.status === status) &&
          key.name.toLowerCase().includes(search.trim().toLowerCase())
      ),
    [keys, status, search]
  );
  const columns = useMemo<ColumnDef<ApiKey>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 220,
        cell: ({ row }) => (
          <span className="block min-w-[180px] font-medium">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'clientId',
        header: 'Client ID',
        size: 200,
        cell: ({ row }) => (
          <code className="text-xs whitespace-nowrap">
            {row.original.clientId ?? 'Not provided'}
          </code>
        ),
      },
      {
        accessorKey: 'clientSecretPreview',
        header: 'Client secret',
        size: 180,
        cell: ({ row }) => (
          <code className="rounded bg-neutral-100 px-2 py-1 text-xs whitespace-nowrap">
            {row.original.clientSecretPreview ?? 'Not provided'}
          </code>
        ),
      },
      {
        accessorKey: 'scopes',
        header: 'Scopes',
        size: 280,
        cell: ({ row }) => <ScopeSummary scopes={row.original.scopes} roles={row.original.roles} />,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        cell: ({ row }) => (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${row.original.status === 'active' ? 'bg-success/10 text-success' : 'bg-neutral-100 text-neutral-600'}`}
          >
            <span className="size-1.5 rounded-full bg-current" />
            {row.original.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires',
        size: 150,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatDate(row.original.expiresAt, 'Not provided')}
          </span>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last used',
        size: 150,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatDate(row.original.lastUsedAt, 'Not provided')}
          </span>
        ),
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        size: 150,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{formatDate(row.original.createdDate, '—')}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        cell: ({ row }) => (
          <div className="flex gap-1 pr-3">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={row.original.status !== 'active'}
              aria-label={`Rotate ${row.original.name}`}
              title="Rotate key"
              onClick={() => setAction({ kind: 'rotate', key: row.original })}
            >
              <ArrowsClockwiseIcon size={16} />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-destructive"
              disabled={row.original.status !== 'active'}
              aria-label={`Revoke ${row.original.name}`}
              title="Revoke key"
              onClick={() => setAction({ kind: 'revoke', key: row.original })}
            >
              <ProhibitIcon size={16} />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };
  const handleIssueKey = async (
    name: string,
    grant: Pick<ApiKeyScopeGrant, 'roles' | 'permissions'>,
    itemId: string
  ) => {
    await createApiKey({
      name,
      roles: grant.roles,
      permissions: grant.permissions,
      itemId,
      organizationId: organizationId ?? '',
    });
    setAction(null);
    resetFilters();
    setNotice('API key issued successfully.');
    void refetch();
  };
  const handleRotateKey = async () => {
    if (action?.kind !== 'rotate') return;
    const key = action.key;
    if (!import.meta.env.VITE_X_BLOCKS_KEY) {
      throw new Error('VITE_X_BLOCKS_KEY is not configured');
    }
    if (!key.accessTokenValidForNumberMinutes || key.accessTokenValidForNumberMinutes <= 0) {
      throw new Error('Token lifetime is missing. Refresh the keys before rotating.');
    }
    if (!key.scopes.length && !key.roles.length) {
      throw new Error('This key has no permissions or roles to copy.');
    }
    await createApiKey({
      itemId: key.itemId,
      name: key.name,
      permissions: key.scopes,
      roles: key.roles,
      accessTokenValidForNumberMinutes: key.accessTokenValidForNumberMinutes,
      organizationId: key.organizationId,
    });
    setAction(null);
    setNotice('API key rotated successfully. Update your integration with the new secret.');
    void refetch();
  };
  const handleRevokeKey = async () => {
    if (action?.kind !== 'revoke') return;
    await revokeApiKey(action.key.itemId);
    setAction(null);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setNotice('API key revoked successfully.');
    void refetch();
  };
  const headerActions = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" disabled={!organizationId} onClick={() => setManageScopes(true)}>
        <SlidersHorizontalIcon size={16} />
        Manage scope permissions
      </Button>
      <Button onClick={() => setAction({ kind: 'issue' })}>
        <PlusIcon size={16} />
        Issue new key
      </Button>
    </div>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">API keys</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Credentials your integrations use to call the API on this organization’s behalf.
          </p>
        </div>
        {headerActions}
      </div>
      {notice && (
        <p role="status" className="text-sm text-success">
          {notice}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlassIcon
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            className="pl-9"
            aria-label="Search API keys by name"
            placeholder="Search by key name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          />
        </div>
        <select
          className={`${selectClass} w-44`}
          aria-label="Filter keys by status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      {!organizationId ? (
        <p role="status" className="rounded-lg border bg-white p-8 text-center">
          Select an organization to view API keys.
        </p>
      ) : isPending ? (
        <div
          role="status"
          aria-label="Loading API keys"
          className="rounded-lg border bg-white p-5 space-y-4"
        >
          <span className="sr-only">Loading API keys</span>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-neutral-100" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-lg border bg-white p-12 text-center space-y-3">
          <WarningCircleIcon size={32} className="mx-auto text-destructive" />
          <h2 className="font-semibold">Couldn&apos;t load API keys</h2>
          <p className="text-sm text-muted-foreground">
            The list of keys couldn’t be fetched. Please try again.
          </p>
          <Button disabled={isFetching} onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : keys.length === 0 ? (
        <div className="rounded-lg border bg-white p-12 text-center space-y-3">
          <KeyIcon size={32} className="mx-auto text-muted-foreground" />
          <h2 className="font-semibold">No API keys yet</h2>
          <p className="mx-auto max-w-md text-sm text-muted-foreground">
            Issue a key so an integration can call the API on your organization’s behalf.
          </p>
          <Button onClick={() => setAction({ kind: 'issue' })}>
            <PlusIcon size={16} /> Issue new key
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border bg-white p-12 text-center space-y-3">
          <MagnifyingGlassIcon size={32} className="mx-auto text-muted-foreground" />
          <h2 className="font-semibold">No matching keys</h2>
          <p className="text-sm text-muted-foreground">Try another name or clear your filters.</p>
          <Button variant="outline" onClick={resetFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <div className="min-w-0 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-neutral-50">
          <AdvanceDataTable
            columns={columns}
            data={rows}
            isExpandRowContent={false}
            pagination={{ ...pagination, totalCount: rows.length }}
            onPaginationChange={setPagination}
            columnPinningConfig={PINNING}
          />
        </div>
      )}
      {action && (
        <ApiKeyActionDialog
          action={action}
          onClose={() => setAction(null)}
          onConfirm={
            action.kind === 'issue'
              ? handleIssueKey
              : action.kind === 'revoke'
                ? handleRevokeKey
                : handleRotateKey
          }
        />
      )}
      {manageScopes && organizationId && (
        <ApiKeyScopeRulesDialog
          organizationId={organizationId}
          onClose={() => setManageScopes(false)}
        />
      )}
    </div>
  );
}
